import { useState } from "react";

import { PlanningScope, PlanningType, RoomType, type Planning, type PutPlanningBody } from "@eisox/api/core-api";
import { Alert, Button, Field, Steps } from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import { PlusIcon } from "@eisox/icons";
import type { z } from "@eisox/zod";
import { ParaglideMessage } from "@inlang/paraglide-js-react";
import { useSelector } from "@tanstack/react-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { planningApi, roomApi } from "~/api";
import { organizePlanningsByWeekDays, UpdatePlanningDialog, Week } from "~/features/planning";
import { PlanningSelectorDialog, RoomsSelector, updateRoomsPlanningsSchema, type Filters } from "~/features/settings";
import { m } from "~/paraglide/messages";
import { useBuildingContext } from "~/providers/building.provider";

export const Route = createFileRoute("/_authenticated/buildings_/$buildingId/settings/rooms/planning")({
  component: RouteComponent,
});

function RouteComponent() {
  const { building, floors, rooms } = useBuildingContext(Route.id);

  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationFn: (body: PutPlanningBody) => planningApi.update(building.id, body),
    onSuccess: async (_, request) => {
      await Promise.all(
        request.parentIds.map(roomId =>
          queryClient.invalidateQueries({ queryKey: roomApi.queries.plannings(roomId).queryKey }),
        ),
      );
    },
  });

  const [filters, setFilters] = useState<Filters>({});

  const form = useAppForm({
    defaultValues: {
      scope: PlanningScope.ROOM,
      parentIds: [] as string[],
      plannings: [] as Planning[],
    },
    validators: {
      onSubmit: updateRoomsPlanningsSchema,
    },
    onSubmit: async ({ formApi, value }) => {
      const { parentIds, plannings } = value;
      await mutateAsync({ scope: PlanningScope.ROOM, parentIds, plannings });
      setFilters({});
      formApi.reset();
    },
  });

  const selectedRoomIds = useSelector(form.store, state => state.values.parentIds);
  const plannings = useSelector(form.store, state => state.values.plannings);

  const selectedRoom = rooms.filter(room => selectedRoomIds.includes(room.id));
  const atLeastOneRoomIsNotABedroom = selectedRoom.some(room => room.type !== RoomType.ROOM);
  const types: PlanningType[] = [
    PlanningType.ABSENCE,
    PlanningType.COMFORT,
    ...(!atLeastOneRoomIsNotABedroom ? ([PlanningType.NIGHT] as const) : []),
  ];

  const { reorderedDays, timeslotsByDay } = organizePlanningsByWeekDays(plannings);

  const handleChangePlannings = (nextPlannings: Planning[]): Promise<void> => {
    const filtered = atLeastOneRoomIsNotABedroom
      ? nextPlannings.filter(planning => planning.type !== "night")
      : nextPlannings;
    form.setFieldValue(
      "plannings",
      filtered.map(p => ({ ...p, userDefined: true })),
    );
    return Promise.resolve();
  };

  return (
    <form
      className="gap-10 flex overflow-auto"
      data-testid="RoomsPlanningSettings"
      onSubmit={e => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppForm>
        <Steps.Root>
          <Steps.Step>
            <Steps.Title>
              <ParaglideMessage
                message={m.create_planning_title}
                markup={{
                  import: ({ children }) => (
                    <PlanningSelectorDialog
                      floors={floors}
                      rooms={rooms}
                      onSelectPlannings={selected => {
                        void handleChangePlannings(selected);
                      }}
                    >
                      {children}
                    </PlanningSelectorDialog>
                  ),
                }}
              />
            </Steps.Title>
            <Steps.Content className="py-2.5 gap-2.5 flex flex-col items-start">
              <UpdatePlanningDialog
                userDefinedPlannings={plannings}
                types={types}
                onValueChange={handleChangePlannings}
              >
                <Button>
                  {m.add_time_slot()} <PlusIcon />
                </Button>
              </UpdatePlanningDialog>
            </Steps.Content>
          </Steps.Step>
          <Steps.Step>
            <Steps.Title>{m.select_rooms()}</Steps.Title>
            <Steps.Content className="py-2.5 gap-2.5 flex flex-col items-start">
              <form.Field name="parentIds">
                {field => (
                  <Field.Root className="w-full">
                    <RoomsSelector.Root
                      rooms={rooms}
                      value={filters}
                      onValueChange={setFilters}
                      onFilteredRoomsChange={rooms => field.handleChange(rooms.map(room => room.id))}
                    >
                      <div className="*:min-w-0 mb-1.25 gap-1.25 flex *:flex-1">
                        <RoomsSelector.TypeSearch />
                        <RoomsSelector.PlanSearch floors={floors} />
                        <RoomsSelector.GroupSearch />
                        <RoomsSelector.PersonnalizedSearch floors={floors} />
                      </div>
                      <RoomsSelector.Result />
                    </RoomsSelector.Root>
                    {(field.state.meta.errors as z.core.$ZodIssue[]).map(error => (
                      <Field.Error key={error.message} match>
                        {error.message}
                      </Field.Error>
                    ))}
                  </Field.Root>
                )}
              </form.Field>
            </Steps.Content>
          </Steps.Step>
          <Steps.Step>
            <Steps.Title>{m.apply_planning()}</Steps.Title>
            <Steps.Content className="py-2.5 gap-2.5 flex flex-col items-start">
              <Alert severity="warning">{m.apply_planning_warning()}</Alert>
              <form.SubmitButton label={m.apply()} />
            </Steps.Content>
          </Steps.Step>
        </Steps.Root>
        <Week.Root>
          {reorderedDays.map(day => (
            <Week.Day key={day} day={day as 0 | 1 | 2 | 3 | 4 | 5 | 6} className="min-h-125 min-w-12.5">
              {(timeslotsByDay[day] ?? []).map((timeslot, index) => (
                <UpdatePlanningDialog
                  key={index}
                  userDefinedPlannings={plannings}
                  types={types}
                  value={timeslot}
                  onValueChange={handleChangePlannings}
                >
                  <Week.Timeslot start={timeslot.hourStart} end={timeslot.hourEnd} type={timeslot.type} userDefined />
                </UpdatePlanningDialog>
              ))}
            </Week.Day>
          ))}
        </Week.Root>
      </form.AppForm>
    </form>
  );
}
